/**
 * Custom sort and filter utilities to replace @progress/kendo-data-query.
 * Drop-in replacements for orderBy, filterBy, SortDescriptor, etc.
 */

import moment from 'moment';

export interface SortDescriptor {
    dir?: 'asc' | 'desc';
    field: string;
}

export interface FilterDescriptor {
    field: string;
    ignoreCase?: boolean;
    operator: string;
    value: any;
}

export interface CompositeFilterDescriptor {
    filters: (FilterDescriptor | CompositeFilterDescriptor)[];
    logic: 'and' | 'or';
}

/**
 * Replacement for @progress/kendo-react-data-tools FieldSettings.
 * Used for advanced search field configuration in table wrappers.
 */
export interface FieldSettings {
    filter?: any;
    label?: string;
    name: string;
    operators?: { operator: string; text: string }[];
}

/**
 * Resolves a nested field value from an object using dot notation.
 * e.g., getNestedValue(obj, 'address.city') => obj.address.city
 */
function getNestedValue(obj: any, field: string): any {

    if (!field) return obj;
    const parts = field.split('.');
    let value = obj;
    for (const part of parts) {

        if (value == null) return undefined;
        value = value[part];

    }
    return value;

}

// Matches the backend report format "MM/dd/yyyy" with optional " hh:mm tt".
const DATE_FORMATS = [
    'M/D/YYYY',
    'M/D/YYYY h:mm A',
    'M/D/YYYY h:mm:ss A',
    'M/D/YYYY h:mmA',
    'M/D/YYYY h:mm:ssA',
    'M/D/YYYY H:mm',
    'M/D/YYYY H:mm:ss'
];

const parseDate = (value: string): number | null => {

    const parsed = moment(value.trim(), DATE_FORMATS, true);
    return parsed.isValid() ? parsed.valueOf() : null;

};

function compareValues(a: any, b: any): number {

    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;

    if (typeof a === 'string' && typeof b === 'string') {

        const aDate = parseDate(a);
        const bDate = parseDate(b);
        if (aDate !== null && bDate !== null) return aDate - bDate;

        return a.localeCompare(b, undefined, { sensitivity: 'base' });

    }

    if (typeof a === 'number' && typeof b === 'number') {

        return a - b;

    }

    if (typeof a === 'boolean' && typeof b === 'boolean') {

        return (a === b) ? 0 : a ? 1 : -1;

    }

    if (a instanceof Date && b instanceof Date) {

        return a.getTime() - b.getTime();

    }

    // Fallback: convert to string
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });

}

/**
 * Sorts an array by one or more sort descriptors.
 * Drop-in replacement for kendo-data-query's orderBy.
 */
export function orderBy<T>(data: T[], descriptors: SortDescriptor[]): T[] {

    if (!descriptors || descriptors.length === 0) return [...data];

    return [...data].sort((a, b) => {

        for (const descriptor of descriptors) {

            const aVal = getNestedValue(a, descriptor.field);
            const bVal = getNestedValue(b, descriptor.field);
            const result = compareValues(aVal, bVal);
            if (result !== 0) {

                return descriptor.dir === 'desc' ? -result : result;

            }

        }
        return 0;

    });

}

/**
 * Custom comparator-based sort for reports.
 * Allows a custom value extractor function.
 */
export function orderByComparator<T>(
    data: T[],
    descriptors: SortDescriptor[],
    getSortableValue: (item: T, field?: string) => any
): T[] {

    if (!descriptors || descriptors.length === 0) return [...data];

    return [...data].sort((a, b) => {

        for (const descriptor of descriptors) {

            const aVal = getSortableValue(a, descriptor.field);
            const bVal = getSortableValue(b, descriptor.field);
            const result = compareValues(aVal, bVal);
            if (result !== 0) {

                return descriptor.dir === 'desc' ? -result : result;

            }

        }
        return 0;

    });

}

// --- Filter utilities ---

function matchesFilter(value: any, filter: FilterDescriptor): boolean {

    const filterValue = filter.value;
    const ignoreCase = filter.ignoreCase !== false;

    let strValue = value != null ? String(value) : '';
    let strFilterValue = filterValue != null ? String(filterValue) : '';

    if (ignoreCase) {

        strValue = strValue.toLowerCase();
        strFilterValue = strFilterValue.toLowerCase();

    }

    switch (filter.operator) {

        case 'eq':
            return value === filterValue || strValue === strFilterValue;
        case 'neq':
            return value !== filterValue && strValue !== strFilterValue;
        case 'contains':
            return strValue.includes(strFilterValue);
        case 'doesnotcontain':
            return !strValue.includes(strFilterValue);
        case 'startswith':
            return strValue.startsWith(strFilterValue);
        case 'endswith':
            return strValue.endsWith(strFilterValue);
        case 'gt':
            return Number(value) > Number(filterValue);
        case 'gte':
            return Number(value) >= Number(filterValue);
        case 'lt':
            return Number(value) < Number(filterValue);
        case 'lte':
            return Number(value) <= Number(filterValue);
        case 'isnull':
            return value == null;
        case 'isnotnull':
            return value != null;
        case 'isempty':
            return strValue === '';
        case 'isnotempty':
            return strValue !== '';
        default:
            return true;

    }

}

function matchesComposite<T>(item: T, filter: CompositeFilterDescriptor): boolean {

    const results = filter.filters.map(f => {

        if ('filters' in f) {

            return matchesComposite(item, f as CompositeFilterDescriptor);

        }
        const fd = f as FilterDescriptor;
        const value = getNestedValue(item, fd.field);
        return matchesFilter(value, fd);

    });

    if (filter.logic === 'and') {

        return results.every(Boolean);

    }
    return results.some(Boolean);

}

/**
 * Filters an array using a CompositeFilterDescriptor or single FilterDescriptor.
 * Drop-in replacement for kendo-data-query's filterBy.
 */
export function filterBy<T>(data: T[], filter: CompositeFilterDescriptor | FilterDescriptor): T[] {

    if (!filter) return [...data];

    if ('filters' in filter) {

        return data.filter(item => matchesComposite(item, filter as CompositeFilterDescriptor));

    }

    const fd = filter as FilterDescriptor;
    return data.filter(item => {

        const value = getNestedValue(item, fd.field);
        return matchesFilter(value, fd);

    });

}

/**
 * Simple text search across all string-coercible fields of an object.
 */
export function textSearch<T>(data: T[], searchTerm: string, fields?: string[]): T[] {

    if (!searchTerm || searchTerm.trim() === '') return data;

    const term = searchTerm.toLowerCase();
    return data.filter(item => {

        const searchableFields = fields || Object.keys(item as any);
        return searchableFields.some(field => {

            const value = getNestedValue(item, field);
            if (value == null) return false;
            return String(value).toLowerCase().includes(term);

        });

    });

}
